import React from 'react';
import styled from 'styled-components';

// Domain schools' IT teams need to allow through their firewall / content
// filter. This is the public-facing JeddAI API hostname we tell admins to
// whitelist — kept separate from whatever endpoint the build is currently
// pointed at (which we also surface below as a diagnostic).
const ALLOWLIST_DOMAIN = 'api.gojeddle.com';
const SUPPORT_EMAIL = 'contact@jeddle.com';

const PRIMARY = '#7200e0';
const TEXT_DARK = '#1e252a';
const TEXT_MUTED = '#56515b';

function getHost(url) {
  try {
    return new URL(url).host;
  } catch (e) {
    return url || 'unknown';
  }
}

/**
 * Shown when the app can't reach the JeddAI backend (e.g. the auth bootstrap
 * call fails). The overwhelmingly common real-world cause is a school or
 * workplace firewall blocking our API, so the copy leads with that and gives
 * IT teams a concrete, copy-pasteable thing to allow.
 */
export default function ConnectionBlocked({ apiBaseUrl, errorDetail }) {
  const endpoint = getHost(apiBaseUrl);

  const supportSubject = encodeURIComponent("I can't connect to JeddAI");
  const supportBody = encodeURIComponent(
    `Hi Jeddle team,\n\n` +
      `I'm seeing a message that JeddAI is blocked by my network and I need help getting connected.\n\n` +
      `Technical details (please don't edit):\n` +
      `- Domain to allow: ${ALLOWLIST_DOMAIN}\n` +
      `- Endpoint reached: ${endpoint}\n` +
      `- Error: ${errorDetail || 'Unable to reach JeddAI'}\n\n` +
      `Thank you!`
  );
  const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${supportSubject}&body=${supportBody}`;

  return (
    <Page>
      <Card>
        <Logo src="/img/logo_with_name.svg" alt="Jeddle" />

        <IconCircle aria-hidden="true">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke={PRIMARY}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9.2" />
            <line x1="2.8" y1="12" x2="21.2" y2="12" />
            <path d="M12 2.8c2.6 2.7 2.6 15.7 0 18.4c-2.6 -2.7 -2.6 -15.7 0 -18.4z" />
            {/* Slash with a same-as-background casing so it cuts a clean gap */}
            <line
              x1="4.6"
              y1="4.6"
              x2="19.4"
              y2="19.4"
              stroke="#f1e6fc"
              strokeWidth="4.5"
            />
            <line
              x1="4.6"
              y1="4.6"
              x2="19.4"
              y2="19.4"
              stroke={PRIMARY}
              strokeWidth="1.9"
            />
          </svg>
        </IconCircle>

        <Title>JeddAI is blocked by your network</Title>

        <Message>
          We couldn&apos;t connect to JeddAI. Your current network is preventing
          access — this is most often caused by a school or workplace firewall.
        </Message>
        <Message>
          If you&apos;re at school, please contact your IT department. You can
          also{' '}
          <SupportLink href={supportHref}>email Jeddle support</SupportLink> and
          we&apos;ll help you get connected.
        </Message>

        <Actions>
          <PrimaryButton type="button" onClick={() => window.location.reload()}>
            Try again
          </PrimaryButton>
          <SecondaryButton href={supportHref}>
            Contact Jeddle support
          </SecondaryButton>
        </Actions>

        <ITPanel>
          <ITHeading>Information for your IT department</ITHeading>
          <ITText>
            JeddAI needs the following domain allowed through your firewall or
            web content filter:
          </ITText>
          <DomainBox>
            <DomainName>{ALLOWLIST_DOMAIN}</DomainName>
            <DomainMeta>HTTPS · port 443</DomainMeta>
          </DomainBox>
          <TechDetails>
            <TechLine>Endpoint reached: {endpoint}</TechLine>
            {errorDetail ? <TechLine>Error: {errorDetail}</TechLine> : null}
          </TechDetails>
        </ITPanel>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 32px 20px;
  background: linear-gradient(180deg, #fcfaff 0%, #f5f0fb 100%);
  font-family: 'IBM Plex Sans', sans-serif;
  z-index: 9999;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 520px;
  margin: auto;
  padding: 40px 36px;
  background: #ffffff;
  border: 1px solid #efeaf6;
  border-radius: 18px;
  box-shadow: 0 12px 40px rgba(56, 26, 125, 0.08);

  @media (max-width: 520px) {
    padding: 32px 22px;
  }
`;

const Logo = styled.img`
  height: 30px;
  margin-bottom: 28px;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #f1e6fc;
  margin-bottom: 22px;
`;

const Title = styled.h1`
  margin: 0 0 14px;
  font-size: 26px;
  line-height: 1.3;
  font-weight: 600;
  color: ${TEXT_DARK};
  text-align: center;

  @media (max-width: 520px) {
    font-size: 22px;
  }
`;

const Message = styled.p`
  margin: 0 0 12px;
  max-width: 420px;
  font-size: 16px;
  line-height: 1.6;
  color: ${TEXT_MUTED};
  text-align: center;
`;

const SupportLink = styled.a`
  color: ${PRIMARY};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin: 14px 0 4px;
`;

const PrimaryButton = styled.button`
  padding: 12px 26px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: ${PRIMARY};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #5e00b8;
  }
`;

const SecondaryButton = styled.a`
  padding: 12px 26px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: ${PRIMARY};
  background: #ffffff;
  border: 1.5px solid #e3d6f5;
  border-radius: 10px;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: #faf6ff;
    border-color: ${PRIMARY};
  }
`;

const ITPanel = styled.div`
  width: 100%;
  margin-top: 30px;
  padding: 20px;
  background: #f8f6fc;
  border: 1px solid #ece7f3;
  border-radius: 12px;
  text-align: left;
`;

const ITHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7b7382;

  &::before {
    content: '🔧';
  }
`;

const ITText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${TEXT_MUTED};
`;

const DomainBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin: 12px 0;
  padding: 11px 14px;
  background: #ffffff;
  border: 1px dashed #d6c8ee;
  border-radius: 8px;
`;

const DomainName = styled.span`
  font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 15px;
  font-weight: 600;
  color: ${TEXT_DARK};
  word-break: break-all;
`;

const DomainMeta = styled.span`
  font-size: 12px;
  color: #7b7382;
`;

const TechDetails = styled.div`
  margin-top: 4px;
`;

const TechLine = styled.div`
  font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #9a93a3;
  word-break: break-all;
`;
